import Box from "@mui/material/Box";
import { Area, Bar, CartesianGrid, ComposedChart, Line, ReferenceArea, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import ChartLegend from "./ChartLegend";

export interface CalendarDatum {
  x: string;
  label: string;
  partial?: boolean;
  weekend?: boolean;
  monthStart?: boolean;
  /** Nome do feriado do dia (vários: separados por " · "). */
  holiday?: string | null;
  expected?: number | null;
  expectedLow?: number | null;
  expectedHigh?: number | null;
  [serie: string]: unknown;
}

export interface CalendarBarsChartProps {
  data: CalendarDatum[];
  bars: Array<{ key: string; label: string; color: string }>;
  showExpected: boolean;
  height?: number;
  width?: number;
  ariaLabel: string;
  valueFormatter?: (v: number) => string;
}

export const CALENDAR_COLORS = { weekend: "#eceff1", holiday: "#fdecea", holidayStroke: "#e57373", monthStart: "#90a4ae", expected: "#17384c" } as const;
export const EXPECTED_LABEL = "esperado (sazonalidade)";

type RectProps = { x?: number; y?: number; width?: number; height?: number };
const rect = (testId: string, fill: string, title?: string) =>
  function CalendarRect({ x = 0, y = 0, width = 0, height = 0 }: RectProps) {
    return (
      <rect data-testid={testId} x={x} y={y} width={Math.max(width, 0)} height={Math.max(height, 0)} fill={fill} fillOpacity={0.9}>
        {title && <title>{title}</title>}
      </rect>
    );
  };

/** "2026-11-01" → "11/26" (rótulo do dia 1º). */
const monthLabel = (x: string) => `${x.slice(5, 7)}/${x.slice(2, 4)}`;

type TipProps = { active?: boolean; label?: string; payload?: Array<{ name?: string; value?: unknown; color?: string; dataKey?: unknown }> };

/**
 * Barras empilhadas de pedidos por dia × calendário: fim de semana em cinza, feriado em vermelho claro
 * (com o nome no tooltip), linha vertical no dia 1º e o "esperado" tracejado com a faixa usual.
 */
export default function CalendarBarsChart({ data, bars, showExpected, height = 300, width, ariaLabel, valueFormatter }: CalendarBarsChartProps) {
  const byX = new Map(data.map((d) => [d.x, d]));
  const labelOf = (x: string) => byX.get(x)?.label ?? x;
  const fmt = (v: number) => (valueFormatter ? valueFormatter(v) : new Intl.NumberFormat("pt-BR", { maximumFractionDigits: 1 }).format(v));
  const hasExpected = showExpected && data.some((d) => d.expected != null);

  const renderTip = ({ active, label, payload }: TipProps) => {
    if (!active || !label) return null;
    const d = byX.get(label);
    return (
      <Box sx={{ bgcolor: "background.paper", border: "1px solid", borderColor: "divider", p: 1, fontSize: 12, borderRadius: "4px" }}>
        <strong>{`${labelOf(label)}${d?.partial ? " (parcial)" : ""}`}</strong>
        {d?.holiday && <Box sx={{ color: "#c62828" }}>{`Feriado: ${d.holiday}`}</Box>}
        {(payload ?? [])
          .filter((p) => typeof p.value === "number")
          .map((p) => (
            <Box key={String(p.name)} sx={{ color: p.color }}>{`${p.name}: ${fmt(p.value as number)}`}</Box>
          ))}
        {hasExpected && d?.expected != null && <Box>{`Esperado: ${fmt(d.expected)}${d.expectedLow != null && d.expectedHigh != null ? ` (${fmt(d.expectedLow)}–${fmt(d.expectedHigh)})` : ""}`}</Box>}
      </Box>
    );
  };

  const chart = (
    <ComposedChart data={data} width={width} height={height} margin={{ top: 16, right: 16, bottom: 8, left: 8 }}>
      <CartesianGrid strokeDasharray="3 3" vertical={false} />
      <XAxis dataKey="x" tickFormatter={labelOf} />
      <YAxis tickFormatter={valueFormatter} />
      {data.map((d) =>
        d.holiday ? (
          <ReferenceArea key={`h-${d.x}`} x1={d.x} x2={d.x} ifOverflow="visible" shape={rect("cal-holiday", CALENDAR_COLORS.holiday, `Feriado: ${d.holiday}`)} />
        ) : d.weekend ? (
          <ReferenceArea key={`w-${d.x}`} x1={d.x} x2={d.x} ifOverflow="visible" shape={rect("cal-weekend", CALENDAR_COLORS.weekend)} />
        ) : null,
      )}
      <Tooltip content={renderTip} />
      {bars.map((b) => (
        <Bar key={b.key} dataKey={b.key} name={b.label} stackId="s" fill={b.color} isAnimationActive={false} />
      ))}
      {hasExpected && (
        <Area dataKey={(d: CalendarDatum) => (d.expectedLow != null && d.expectedHigh != null ? [d.expectedLow, d.expectedHigh] : null)} name="faixa usual"
          stroke="none" fill={CALENDAR_COLORS.expected} fillOpacity={0.15} isAnimationActive={false} activeDot={false} tooltipType="none" />
      )}
      {hasExpected && (
        <Line dataKey="expected" name={EXPECTED_LABEL} stroke={CALENDAR_COLORS.expected} strokeWidth={2} strokeDasharray="5 4" dot={false} isAnimationActive={false} tooltipType="none" connectNulls={false} />
      )}
      {data.filter((d) => d.monthStart).map((d) => (
        <ReferenceLine key={`m-${d.x}`} x={d.x} stroke={CALENDAR_COLORS.monthStart} strokeDasharray="4 3" ifOverflow="visible"
          label={{ value: monthLabel(d.x), position: "insideTopLeft", fontSize: 11, fill: "#546e7a" }} />
      ))}
    </ComposedChart>
  );
  return (
    <Box role="figure" aria-label={ariaLabel} sx={{ width: width ? `${width}px` : "100%" }}>
      {width ? chart : <ResponsiveContainer width="100%" height={height}>{chart}</ResponsiveContainer>}
      <ChartLegend
        items={[
          ...bars.map((b) => ({ label: b.label, color: b.color, kind: "bar" as const })),
          ...(hasExpected ? [{ label: EXPECTED_LABEL, color: CALENDAR_COLORS.expected, dashed: true }] : []),
          { label: "feriado", color: CALENDAR_COLORS.holidayStroke, kind: "bar" as const },
          { label: "fim de semana", color: "#90a4ae", kind: "bar" as const },
        ]}
      />
    </Box>
  );
}
